import { FiltersDefinition, SortingsDefinition } from '@factorialco/f0-react'
import { OneDataCollection, SummariesDefinition } from '@factorialco/f0-react/dist/experimental'
import { useNavigate } from 'react-router-dom'

import { HeadcountReporteesSummariesDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'

type DirectReporteeRow = {
  id: string
  firstName: string
  lastName: string
  avatarUrl: string | null
  role: string
  forecastedCost: string
  requestsCount: number
  peopleCount: number
  forecastedHeadcount: number
}

interface Props {
  periodId: string
  planningId?: string
  currency?: string
}

const formatMoney = (amount: number, currency: string): string => {
  if (isNaN(amount)) return '-'

  try {
    return new Intl.NumberFormat(i18n.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return String(amount)
  }
}

const DirectReporteesTable = ({ periodId, planningId, currency = 'EUR' }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const source = useGraphqlDataCollectionSource<
    DirectReporteeRow,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    typeof HeadcountReporteesSummariesDocument
  >(
    {
      document: HeadcountReporteesSummariesDocument,
      mapData: (data) => {
        const edges = data.planning.reporteesSummariesConnection?.edges ?? []

        return edges.map((edge) => {
          const node = edge.node
          const emp = node.employee
          const costInCents = parseFloat(node.forecastedCost)

          return {
            id: emp.id,
            firstName: emp.firstName,
            lastName: emp.lastName,
            avatarUrl: emp.avatar?.url ?? null,
            role: getEmployeeJobRole(emp.referenceContract),
            forecastedCost: formatMoney(costInCents / 100, currency),
            requestsCount: node.requestsCount,
            peopleCount: node.peopleCount,
            forecastedHeadcount: node.requestsCount + node.peopleCount,
          }
        })
      },
      mapVariables: () => ({
        planningPeriodId: periodId,
        planningPlanningId: planningId,
      }),
      mapPagination: (data) => ({
        total: data.planning.reporteesSummariesConnection?.totalCount ?? 0,
      }),
      itemOnClick: (item) => () => {
        const employeeSheetPath =
          navigation.finance.financialWorkspace.headcountPlanning.periodDetail.employeeSheet.fullPath(
            {
              periodId,
            }
          )

        navigate(`${employeeSheetPath}?managerId=${encodeURIComponent(item.id)}`)
      },
      paginationType: 'pages',
      search: { enabled: true },
    },
    [currency, navigate, navigation, periodId, planningId]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table' as const,
          options: {
            columns: [
              {
                label: i18n.t('planning.direct_reportees_table.columns.name'),
                render: (item) => ({
                  type: 'person' as const,
                  value: {
                    firstName: item.firstName,
                    lastName: item.lastName,
                    src: buildUrl(item.avatarUrl),
                  },
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.role'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.role,
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.forecasted_cost'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: item.forecastedCost,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.new_headcount'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: String(item.requestsCount),
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.actual_headcount'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: String(item.peopleCount),
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.forecasted_headcount'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: String(item.forecastedHeadcount),
                }),
              },
            ],
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.direct_reportees_table.empty_state.title'),
          description: i18n.t('planning.direct_reportees_table.empty_state.description'),
        },
      }}
    />
  )
}

export default DirectReporteesTable
